import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo, useRef } from 'react';

import { useGetIntegrationAuthStateQuery } from '@app/api/SocialIntegrations';
import { ACTIVE, ERROR, READY } from '@app/constants';

import {
  AuthStepState,
  type AuthStepInfo,
  type IntegrationAuthSubStatus,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/types';

const POLLING_INTERVAL_MS = 2500;

const SUB_STATUS_TO_STATE: Partial<
  Record<IntegrationAuthSubStatus, AuthStepState>
> = {
  qr_ready: AuthStepState.QrReady,
  qr_scanned: AuthStepState.QrScanned,
  pending_code: AuthStepState.CodeSent,
  pending_password: AuthStepState.Password2fa,
  reconnecting: AuthStepState.Loading,
  auth_failed: AuthStepState.Error,
};

interface UseIntegrationAuthStateParams {
  integrationId: number | null;
  activeBranchId: number | null;
  /** Поллинг активен только пока открыт шаг «Авторизация» */
  isActive: boolean;
}

/**
 * Текущее состояние шага входа: поллинг GetIntegrationAuthState раз в
 * 2,5 секунды. Push (SocialIntegrationStateChanged с SubStatus) приходит
 * через существующую подписку useIntegrations и просто опережает поллинг —
 * отдельного канала каркасу не нужно.
 */
export const useIntegrationAuthState = ({
  integrationId,
  activeBranchId,
  isActive,
}: UseIntegrationAuthStateParams): AuthStepInfo => {
  const queryIdentity =
    isActive && integrationId && activeBranchId
      ? `${activeBranchId}:${integrationId}`
      : null;
  const authSessionRef = useRef<{ identity: string | null; startedAt: number }>(
    {
      identity: null,
      startedAt: 0,
    }
  );

  // Открытие окна и смена интеграции — новая auth-сессия. RTK Query хранит
  // успешный ответ прежнего открытия того же arg, а `data` при смене arg ещё
  // и переносит ответ другого аккаунта. До свежего ответа такой кэш нельзя
  // принимать за Success/Error текущей попытки.
  if (authSessionRef.current.identity !== queryIdentity) {
    authSessionRef.current = {
      identity: queryIdentity,
      startedAt: queryIdentity ? Date.now() : 0,
    };
  }

  const { currentData, fulfilledTimeStamp, isError } =
    useGetIntegrationAuthStateQuery(
      isActive && integrationId && activeBranchId
        ? { ActiveBranchId: activeBranchId, integrationId }
        : skipToken,
      {
        pollingInterval: POLLING_INTERVAL_MS,
        refetchOnMountOrArgChange: true,
      }
    );

  const data =
    queryIdentity &&
    fulfilledTimeStamp !== undefined &&
    fulfilledTimeStamp >= authSessionRef.current.startedAt
      ? currentData
      : undefined;

  return useMemo<AuthStepInfo>(() => {
    // RTK Query сохраняет последний успешный currentData, когда очередной
    // polling-refetch завершился ошибкой. Такой транспортный сбой не имеет
    // права откатывать уже подтверждённый шаг (особенно qr_scanned/success) в
    // Error: пользователь мог нажать повтор и инвалидировать живую попытку.
    // Ошибку показываем только пока в текущей auth-сессии ещё нет ни одного
    // свежего авторитетного ответа.
    if (isError && !data) {
      return {
        state: AuthStepState.Error,
        errorMessage:
          'Не удалось получить состояние подключения, попробуйте ещё раз',
      };
    }

    if (!data) {
      return { state: AuthStepState.Loading };
    }

    if (data.Status === ACTIVE || data.Status === READY) {
      return { state: AuthStepState.Success };
    }

    const bySubStatus =
      data.SubStatus &&
      SUB_STATUS_TO_STATE[data.SubStatus as IntegrationAuthSubStatus];

    if (bySubStatus) {
      return { state: bySubStatus };
    }

    // Создание инстанса в шлюзе упало: интеграция в error без шага входа.
    // Раньше это состояние проваливалось в Loading, окно поллило мёртвую
    // интеграцию и наружу уходил жаргон резолва токена (issue/10880).
    if (data.Status === ERROR) {
      return {
        state: AuthStepState.Error,
        errorMessage: 'Создание интеграции не завершилось — попробуйте позже.',
      };
    }

    return { state: AuthStepState.Loading };
  }, [data, isError]);
};
